import type {
  ConfigurationParameters,
  SetPasswordData,
  SignupData,
  UserPreferencesUpdateData,
  UserProfileData,
  UserProfileUpdateData,
} from '@mogul/api-client';
import {
  AuthApi,
  Configuration,
  ErrorCode,
  PlatformRole,
  ViewerApi,
} from '@mogul/api-client';
import Cookies from 'js-cookie';
import * as r from 'runtypes';
import { BehaviorSubject } from 'rxjs';

import { Cookie } from '../constants/cookie.constants';
import { parseApiErrors } from '../utils/api.util';
import { verifyToken } from '../utils/jwt.util';

export enum AuthState {
  initial = 'initial',
  unauthenticated = 'unauthenticated',
  mfaRequired = 'mfaRequired',
  verificationRequired = 'verificationRequired',
  authenticated = 'authenticated',
}

export interface AuthContext {
  accessToken?: string;
  refreshToken?: string;
  mfaToken?: string;
  unverifiedEmail?: string;
  otpSecret?: string;
  otpAuthUrl?: string;
  user?: UserProfileData;
}

export const savedAuthContextType = r.Record({
  a: r.String.optional(),
  r: r.String.optional(),
  e: r.String.optional(),
  u: r
    .Record({
      email: r.String,
      mfaActive: r.Boolean,
      id: r.String,
      createdAt: r.String,
      updatedAt: r.String,
      name: r.String.nullable(),
      username: r.String,
      role: r.Union(
        r.Literal(PlatformRole.None),
        r.Literal(PlatformRole.Read),
        r.Literal(PlatformRole.Manage),
        r.Literal(PlatformRole.Admin),
      ),
    })
    .optional(),
});

export class AuthMachine {
  readonly state;
  readonly context;

  constructor(readonly options: { apiBasePath: string }) {
    this.context = new BehaviorSubject<AuthContext>(this.loadContext());
    this.context.subscribe(context => this.saveContext(context));

    const { accessToken, refreshToken, unverifiedEmail } = this.context.value;

    const authenticated = verifyToken(accessToken);

    let initialState;

    if (authenticated) {
      initialState = AuthState.authenticated;
    } else if (refreshToken !== undefined) {
      initialState = AuthState.initial;
      void this.refresh();
    } else if (unverifiedEmail !== undefined) {
      initialState = AuthState.verificationRequired;
    } else {
      initialState = AuthState.unauthenticated;
    }

    this.state = new BehaviorSubject<AuthState>(initialState);
  }

  private loadContext(): AuthContext {
    const context: unknown = Cookies.getJSON(Cookie.auth);

    if (savedAuthContextType.guard(context)) {
      return {
        accessToken: context.a,
        refreshToken: context.r,
        unverifiedEmail: context.e,
        user: context.u,
      };
    }

    return {};
  }

  private saveContext(context: AuthContext) {
    Cookies.set(Cookie.auth, {
      a: context.accessToken,
      r: context.refreshToken,
      e: context.unverifiedEmail,
      u: context.user,
    });
  }

  private getAuthApi(config: ConfigurationParameters) {
    return new AuthApi(
      new Configuration({
        ...config,
        basePath: this.options.apiBasePath,
      }),
    );
  }

  private getViewerApi(config: ConfigurationParameters) {
    return new ViewerApi(
      new Configuration({
        ...config,
        basePath: this.options.apiBasePath,
      }),
    );
  }

  private async getToggleEmailNotify(
    userPreferencesUpdateData: UserPreferencesUpdateData,
  ) {
    const viewerApi = this.getViewerApi({
      accessToken: this.context.value.accessToken,
    });

    await viewerApi.toggleEmailNotify({
      userPreferencesUpdateData,
    });

    const user = (await viewerApi.getViewer()).data.data;
    this.context.next({ ...this.context.value, user });
  }

  private async updateProfile(userProfileUpdateData: UserProfileUpdateData) {
    const viewerApi = this.getViewerApi({
      accessToken: this.context.value.accessToken,
    });

    await viewerApi.updateViewerProfile({ userProfileUpdateData });

    const user = (await viewerApi.getViewer()).data.data;
    this.context.next({ ...this.context.value, user });
  }

  private async signup(signupData: SignupData) {
    const authApi = this.getAuthApi({});

    const user = (await authApi.signup({ signupData })).data.data;

    this.context.next({ user, unverifiedEmail: user.email });
    this.state.next(AuthState.verificationRequired);
  }

  private async verifyMfa(data: { otpCode: string }) {
    const { mfaToken } = this.context.value;

    const { accessToken = mfaToken } = this.context.value;

    const authApi = this.getAuthApi({ accessToken, apiKey: data.otpCode });
    const session = (await authApi.login()).data.data;

    const viewerApi = this.getViewerApi({ accessToken: session.accessToken });
    const user = (await viewerApi.getViewer()).data.data;

    this.context.next({
      ...this.context.value,
      accessToken: session.accessToken,
      refreshToken: session.refreshToken,
      user,
    });
    this.state.next(AuthState.authenticated);
  }

  private async login(config: ConfigurationParameters) {
    let accessToken;
    let refreshToken;
    let user;

    try {
      const authApi = this.getAuthApi(config);
      ({ accessToken, refreshToken } = (await authApi.login()).data.data);
      const viewerApi = this.getViewerApi({ accessToken });
      user = (await viewerApi.getViewer()).data.data;
    } catch (error: unknown) {
      for (const { code, meta } of parseApiErrors(error)) {
        if (code === ErrorCode.AuthMfaRequired) {
          const mfaToken = meta?.mfaToken;
          if (typeof mfaToken === 'string') {
            this.context.next({ ...this.context.value, mfaToken });
            this.state.next(AuthState.mfaRequired);
            return;
          }
        }

        if (code === ErrorCode.AuthEmailVerificationRequired) {
          const email = meta?.email;
          if (typeof email === 'string') {
            this.context.next({
              ...this.context.value,
              unverifiedEmail: email,
            });
            this.state.next(AuthState.verificationRequired);
            return;
          }
        }

        if (
          code === ErrorCode.AuthUnauthorized &&
          verifyToken(this.context.value.mfaToken)
        ) {
          this.state.next(AuthState.mfaRequired);
          if (this.state.getValue() === AuthState.unauthenticated) {
            return;
          } else {
            throw error;
          }
        }
      }

      this.state.next(AuthState.unauthenticated);

      throw error;
    }

    this.context.next({
      ...this.context.value,
      accessToken,
      refreshToken,
      user,
    });
    this.state.next(AuthState.authenticated);
  }

  private reset() {
    this.context.next({});
    this.state.next(AuthState.unauthenticated);
  }

  private async logout() {
    // The logout API endpoint works by invalidating the current session
    // refresh token record in our DB, preventing new access tokens from being
    // generated. The current access token remains valid for the remainder of
    // its (short) lifespan after logout and it is simply discarded when the
    // current auth cache cookie is cleared after the logout call succeeds.

    // The logout endpoint supports authentication using either an access
    // token or session refresh token. We first attempt using the access
    // token to authenticate - but this may fail. We then failover to using
    // the session refresh token if the access token request fails for any
    // reason (e.g. the access token has expired). The effect is the same
    // in both cases and only one successful call with either authentication
    // method is necessary.

    let error: unknown;
    let failed = false;

    const { accessToken, refreshToken } = this.context.value;

    if (accessToken !== undefined) {
      try {
        const authApi = this.getAuthApi({ accessToken });
        await authApi.logout();
      } catch (e) {
        error = e;
        failed = true;
      }
    }

    if (failed && refreshToken !== undefined) {
      try {
        const authApi = this.getAuthApi({ apiKey: refreshToken });
        await authApi.logout();
      } catch (e) {
        error = e;
        failed = true;
      }
    }

    this.reset();

    if (failed) {
      throw error;
    }
  }

  private async refresh() {
    const { refreshToken } = this.context.value;

    let accessToken;
    let user;

    try {
      const authApi = this.getAuthApi({
        basePath: this.options.apiBasePath,
        apiKey: refreshToken,
      });
      ({ accessToken } = (await authApi.createToken()).data.data);

      const viewerApi = this.getViewerApi({ accessToken });
      user = (await viewerApi.getViewer()).data.data;
    } catch (error: unknown) {
      this.state.next(AuthState.unauthenticated);
      throw error;
    }

    this.context.next({ ...this.context.value, accessToken, user });
    this.state.next(AuthState.authenticated);
  }

  async initMfa() {
    const { accessToken } = this.context.value;
    const viewerApi = this.getViewerApi({ accessToken });

    return (await viewerApi.initMfa()).data.data;
  }

  async disableMfa() {
    const { accessToken } = this.context.value;
    const viewerApi = this.getViewerApi({ accessToken });
    await viewerApi.disableMfa();

    const user = (await viewerApi.getViewer()).data.data;
    this.context.next({ ...this.context.value, user });
  }

  async setPassword(setPasswordData: SetPasswordData) {
    const { accessToken } = this.context.value;
    const viewerApi = this.getViewerApi({ accessToken });
    await viewerApi.setViewerPassword({ setPasswordData });
  }

  async verifyEmail(data: { token: string }) {
    const authApi = this.getAuthApi({ apiKey: data.token });
    await authApi.verify();

    this.reset();
  }

  async getAccessToken() {
    const { accessToken, refreshToken } = this.context.value;

    if (accessToken !== undefined && verifyToken(accessToken)) {
      return accessToken;
    }

    if (refreshToken !== undefined) {
      await this.refresh();
    }

    return this.context.value.accessToken;
  }

  actions = {
    signup: this.signup.bind(this),

    login: async (data: { username: string; password: string }) => {
      await this.login(data);
    },

    updateProfile: this.updateProfile.bind(this),

    setPassword: this.setPassword.bind(this),

    initMfa: this.initMfa.bind(this),

    verifyMfa: this.verifyMfa.bind(this),

    verifyEmail: this.verifyEmail.bind(this),

    disableMfa: this.disableMfa.bind(this),

    reset: this.reset.bind(this),

    logout: this.logout.bind(this),

    getToggleEmailNotify: this.getToggleEmailNotify.bind(this),
  };
}
